import React from 'react';
import PropTypes from 'prop-types';
import { Box, Typography, Paper, Button } from '@mui/material';
import LockIcon from '@mui/icons-material/Lock';
import { useMenuAccess } from '../../client/hooks/access/useMenuAccess';

/**
 * Componente que protege o acesso a funcionalidades baseado em permissões
 */
const AccessGuard = ({ 
  children, 
  feature, 
  fallback = null,
  showMessage = true 
}) => {
  const { checkFeatureAccess, organizationId } = useMenuAccess();

  const hasAccess = checkFeatureAccess(feature);

  if (hasAccess) {
    return children;
  }

  // Se fornecido um fallback customizado, usa ele
  if (fallback) {
    return fallback;
  }

  // Se não deve mostrar mensagem, retorna null
  if (!showMessage) {
    return null;
  }

  // Mensagem padrão de acesso negado
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '400px',
        p: 3,
      }}
    >
      <Paper
        elevation={3}
        sx={{
          p: 4,
          textAlign: 'center',
          maxWidth: 500,
          borderRadius: 3,
          background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
        }}
      >
        <Box
          sx={{
            width: 80,
            height: 80,
            borderRadius: '50%',
            bgcolor: 'rgba(255, 112, 67, 0.1)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto',
            mb: 3,
          }}
        >
          <LockIcon sx={{ fontSize: 40, color: '#ff7043' }} />
        </Box>
        
        <Typography 
          variant="h5" 
          gutterBottom 
          sx={{ fontWeight: 600, color: '#424242' }}
        >
          Acesso Restrito
        </Typography>
        
        <Typography 
          variant="body1" 
          color="text.secondary" 
          paragraph
          sx={{ mb: 3 }}
        >
          Esta funcionalidade não está disponível para sua organização.
          Entre em contato com o suporte para mais informações.
        </Typography>

        <Typography 
          variant="body2" 
          color="text.secondary"
          sx={{ 
            fontSize: '0.875rem',
            opacity: 0.7,
            mt: 2
          }}
        >
          Organização ID: {organizationId || 'Não identificada'}
        </Typography>

        <Button
          variant="outlined"
          sx={{ mt: 2 }}
          onClick={() => window.history.back()}
        >
          Voltar
        </Button>
      </Paper>
    </Box>
  );
};

AccessGuard.propTypes = {
  children: PropTypes.node.isRequired,
  feature: PropTypes.string.isRequired,
  fallback: PropTypes.node,
  showMessage: PropTypes.bool,
};

export default AccessGuard;